"use client";

import { useEffect, useState } from "react";
import PrimaryButton from "@/components/ui/PrimaryButton";

/**
 * @file        src/components/admin/RegenerateCoverButton.tsx
 * @description Admin-only "regenerate this cover" control for an article (kind "blog") or a news
 *              item (kind "news"). Both pages are statically pre-rendered, so admin status is
 *              resolved on the CLIENT via GET /api/admin/me; the action POSTs {slug, kind} to
 *              /api/admin/regenerate-cover.
 * @rule        Renders NOTHING for anonymous/non-admin visitors (fail-closed). It is a UX affordance
 *              only — the mutation route enforces requireAdminAuth (INV-S9 / R-ADMIN-1).
 * @rule        Маршрут ставит задание в очередь, а рисует отдельный процесс, поэтому ответ один —
 *              "queued", и подпись говорит про очередь, а не про деплой (ADR 2026-09-21-2).
 * @universal   knowledge/universals/components.md#regenerate-cover-button
 */

type Status =
  | "checking"
  | "hidden"
  | "idle"
  | "loading"
  | "queued"
  | "error";

interface RegenerateCoverButtonProps {
  slug: string;
  /** Which collection the material belongs to */
  kind: "blog" | "news";
}

const STATUS_TEXT: Partial<Record<Status, string>> = {
  queued: "Задание в очереди — обложка появится через несколько минут, обновите страницу",
  error: "Ошибка, попробуйте ещё раз",
};

export default function RegenerateCoverButton({ slug, kind }: RegenerateCoverButtonProps) {
  const [status, setStatus] = useState<Status>("checking");

  // Client-side admin check on mount, once. Fail-closed: any failure → hidden.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/admin/me")
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("me failed"))))
      .then((data: { isAdmin?: boolean }) => {
        if (!cancelled) setStatus(data?.isAdmin === true ? "idle" : "hidden");
      })
      .catch(() => {
        if (!cancelled) setStatus("hidden");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Anonymous / non-admin / still checking → render nothing (no layout shift).
  if (status === "checking" || status === "hidden") return null;

  async function handleRegenerate() {
    setStatus("loading");
    try {
      const res = await fetch("/api/admin/regenerate-cover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, kind }),
      });
      const data = res.ok ? await res.json().catch(() => null) : null;
      if (data?.status === "queued") setStatus("queued");
      else setStatus("error");
    } catch {
      setStatus("error");
    }
  }

  const busy = status === "loading";
  // Clickable when idle or after an error (retry). queued locks it.
  const clickable = status === "idle" || status === "error";
  const statusText = STATUS_TEXT[status];

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {/* Visible control is the PrimaryButton universal (single button look). */}
      <PrimaryButton
        onClick={handleRegenerate}
        disabled={!clickable}
        className="disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? "Запускаю…" : "Перегенерировать обложку"}
      </PrimaryButton>
      {statusText && <span className="text-xs text-text-muted">{statusText}</span>}
    </div>
  );
}
